import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'
import { SignInButton } from '@/components/sign-in-button'
import {
  IconCheck,
  IconSparkles,
  IconMoonStars,
  IconArchive,
  IconTrash,
  IconArrowRight,
  IconPlus,
  IconShieldCheck,
  IconX
} from '@tabler/icons-react'

export const Route = createFileRoute('/features')({
  component: FeaturesPage,
})

function FeaturesPage() {
  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-950">
      <StaticNav />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-blue-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-white border border-slate-200 shadow-sm">
              <IconSparkles className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-slate-700">
                The Operating System for your Inbox
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 font-['Switzer',_sans-serif] tracking-[-0.01em]">
              Every feature you need to win back your morning.
            </h1>

            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Winbox Zero combines the familiarity of Gmail with the intelligence of modern LLMs to automate your inbox cleanup and keep you focused.
            </p>
            
            <div className="flex justify-center">
              <SignInButton location="hero" />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-32">
        
        {/* Feature 1: Intelligence & Reply Priority */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Important threads surface first.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Our AI analyzes your historical reply patterns. It learns which senders and topics you actually care about, pinning critical emails to the top of your inbox while burying newsletters and cold outreach.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Identifies VIP contacts and teammates</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Auto-generates labels based on context</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Learns from your daily corrections</li>
            </ul>
          </div>
          
          {/* Visual Mockup */}
          <div className="relative rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl bg-blue-50/50 border border-blue-100/50">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">Sarah from Engineering</p>
                      <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Priority</span>
                    </div>
                    <p className="text-xs text-slate-500">Need your review on the Q3 roadmap ASAP.</p>
                  </div>
                </div>
                <span className="text-xs font-medium text-slate-400">8m ago</span>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-xl border border-slate-100 opacity-60 grayscale">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-transparent" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-600">Growth Weekly</p>
                      <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Newsletter</span>
                    </div>
                    <p className="text-xs text-slate-400">10 tips for scaling your startup...</p>
                  </div>
                </div>
                <span className="text-xs font-medium text-slate-400">2h ago</span>
              </div>
            </div>
            
            {/* Annotation */}
            <div className="absolute -right-6 -bottom-6 bg-white border border-slate-200 shadow-lg rounded-xl p-4 transform rotate-3">
              <p className="text-xs font-semibold text-slate-900 flex items-center gap-1.5">
                <IconSparkles className="w-4 h-4 text-amber-500" />
                AI sorted this for you
              </p>
            </div>
          </div>
        </section>

        {/* Feature 2: Visual Kanban */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative rounded-3xl border border-slate-200 bg-slate-50 p-6 overflow-hidden">
            <div className="flex gap-4 w-[150%]">
              {['Inbox', 'Unsubscribe', 'Archive', 'Delete'].map((col, i) => (
                <div key={col} className="w-48 bg-white border border-slate-200 rounded-xl flex flex-col shrink-0">
                  <div className="p-3 border-b border-slate-100 bg-slate-50/50 rounded-t-xl flex justify-between items-center">
                    <span className="text-xs font-semibold text-slate-700">{col}</span>
                    <span className="text-[10px] text-slate-400 bg-white px-1.5 py-0.5 rounded shadow-sm border border-slate-100">8</span>
                  </div>
                  <div className="p-2 space-y-2">
                    <div className="h-12 bg-slate-50 rounded-lg border border-slate-100 p-2">
                      <div className="h-2 w-1/2 bg-slate-200 rounded mb-2" />
                      <div className="h-2 w-3/4 bg-slate-100 rounded" />
                    </div>
                    {i === 2 && (
                      <div className="h-12 bg-white shadow-lg ring-1 ring-blue-500/20 rounded-lg p-2 transform rotate-3 scale-105 z-10 cursor-grabbing">
                        <div className="h-2 w-1/2 bg-blue-200 rounded mb-2" />
                        <div className="h-2 w-3/4 bg-blue-100 rounded" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-slate-50 to-transparent pointer-events-none" />
          </div>

          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Visual Kanban sorting.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Review your inbox visually. Winbox Zero pre-sorts your mail into actionable columns. Simply drag and drop emails between columns to reassign them, correcting the AI and training it for tomorrow.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-purple-600" /> Triage emails at light speed</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-purple-600" /> Drag-and-drop workflow</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-purple-600" /> Perfect top-down visibility</li>
            </ul>
          </div>
        </section>

        {/* Feature 3: One-Click Mass Unsubscribe */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">One-click mass unsubscribe.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Stop hunting for tiny, hidden unsubscribe links at the bottom of marketing emails. We identify every mailing list, newsletter, and promotional sender in your inbox, allowing you to purge them all at once.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-red-500" /> Identifies hidden mailing lists</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-red-500" /> Processes unsubscribe requests automatically</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-red-500" /> Keeps your email safely out of databases</li>
            </ul>
          </div>
          
          <div className="relative rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
            <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-4">
              <h3 className="font-semibold text-slate-900">Unsubscribe Queue</h3>
              <button className="bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5">
                <IconTrash className="w-3.5 h-3.5" /> Unsubscribe All (24)
              </button>
            </div>
            <div className="space-y-2">
              {[
                { name: 'Target Weekly Ad', type: 'Marketing' },
                { name: 'Sephora Beauty Insider', type: 'Promo' },
                { name: 'Substack Recommendations', type: 'Newsletter' }
              ].map((sender, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" defaultChecked className="rounded border-slate-300 text-red-500 focus:ring-red-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{sender.name}</p>
                      <p className="text-xs text-slate-500">{sender.type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature 4: Bulk Archive & Delete */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative rounded-3xl border border-slate-200 bg-slate-900 p-8 shadow-2xl overflow-hidden flex items-center justify-center min-h-[300px]">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10" />
            
            <div className="relative z-10 flex items-center gap-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 shadow-2xl transform hover:scale-105 transition-transform">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
                <IconArchive className="h-5 w-5" />
              </div>
              <div>
                <p className="text-white font-medium">Batch Action Complete</p>
                <p className="text-white/60 text-sm">Archived 1,248 older emails</p>
              </div>
            </div>

            <div className="absolute top-8 left-8 w-12 h-8 bg-white/5 rounded border border-white/10 transform -rotate-12 blur-[1px]" />
            <div className="absolute bottom-12 right-12 w-16 h-10 bg-white/5 rounded border border-white/10 transform rotate-6 blur-[2px]" />
          </div>

          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Clear years of clutter instantly.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Batch execute hundreds or thousands of emails with a single click. Clear out expired promotions, old calendar invites, and read notifications without the tedious page-by-page cleanup native to Gmail.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Execute decisions across entire columns</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Bypass Gmail's 50-email page limits</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Reach Inbox Zero in seconds</li>
            </ul>
          </div>
        </section>

        {/* Feature 5: Plain-English Automations */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Automate with plain English.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Stop wrestling with complex filter builders. Just type what you want to happen, and our AI converts it into robust, reliable routing rules that run automatically every morning.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-emerald-600" /> "Archive emails with PDF attachments"</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-emerald-600" /> "Emails from Uber go to Travel"</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-emerald-600" /> Evaluated instantly using local logic</li>
            </ul>
          </div>
          
          <div className="relative rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-inner">
                <p className="text-slate-900 font-medium font-serif italic">
                  "If sender is Uber or Lyft, label it as Travel"
                </p>
              </div>
              
              <div className="flex justify-center text-slate-300">
                <IconArrowRight className="h-6 w-6 transform rotate-90" />
              </div>
              
              <div className="bg-slate-900 rounded-xl p-4 font-mono text-sm shadow-md">
                <div className="flex gap-2">
                  <span className="text-purple-400">IF</span>
                  <span className="text-blue-300">sender.domain</span>
                  <span className="text-slate-400">IN</span>
                  <span className="text-amber-300">['uber.com', 'lyft.com']</span>
                </div>
                <div className="flex gap-2 mt-2 pt-2 border-t border-slate-800">
                  <span className="text-purple-400">THEN</span>
                  <span className="text-emerald-400">apply_label</span>
                  <span className="text-amber-300">"Travel"</span>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Feature 9: Explainable AI */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Explainable AI decisions.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Never wonder why an email was moved. Click the AI spark icon on any conversation to see exactly how our model weighed the metadata, sender history, and email intent to make its sorting decision.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-amber-500" /> Transparent reasoning for every action</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-amber-500" /> See exact confidence scores</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-amber-500" /> Understand how your rules fired</li>
            </ul>
          </div>
          
          <div className="flex justify-center">
            <div className="bg-slate-900 rounded-2xl p-6 shadow-2xl border border-slate-800 w-full max-w-sm relative">
              <div className="absolute -top-3 left-8 border-[8px] border-transparent border-b-slate-900" />
              <div className="flex items-center gap-2 mb-4 border-b border-slate-800 pb-3">
                <IconSparkles className="w-4 h-4 text-amber-400" />
                <p className="text-xs text-slate-300 uppercase tracking-wider font-semibold">AI Analysis Log</p>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-white"><span className="text-purple-400 font-medium">History:</span> Frequent marketing list (2% open rate)</p>
                <p className="text-sm text-white"><span className="text-blue-400 font-medium">Intent:</span> Promotional discount inside</p>
                <p className="text-sm text-white"><span className="text-emerald-400 font-medium">Confidence:</span> 98.4% Match</p>
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <p className="text-sm text-white flex items-center gap-2">
                    <span className="text-amber-400 font-medium">Decision:</span> 
                    <span className="bg-slate-800 px-2 py-0.5 rounded text-slate-300 text-xs">Move to Unsubscribe</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature 6: Custom Columns */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative rounded-3xl border border-slate-200 bg-slate-50 p-6 overflow-hidden">
            <div className="flex gap-3">
              <div className="w-1/3 bg-white border border-slate-200 rounded-xl p-3 shadow-sm opacity-50">
                <span className="text-xs font-semibold text-slate-700">Inbox</span>
              </div>
              <div className="w-1/3 bg-white border-2 border-blue-500 rounded-xl p-3 shadow-md relative">
                <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</div>
                <span className="text-xs font-semibold text-blue-700">Receipts</span>
                <div className="mt-3 space-y-2">
                  <div className="h-8 bg-slate-50 rounded border border-slate-100" />
                  <div className="h-8 bg-slate-50 rounded border border-slate-100" />
                </div>
              </div>
              <div className="w-1/3 border-2 border-dashed border-slate-300 rounded-xl p-3 flex flex-col items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-400 cursor-pointer transition-colors bg-white/50">
                <IconPlus className="h-6 w-6 mb-1" />
                <span className="text-xs font-medium">Add Column</span>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Build your perfect workflow.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Don't conform to our setup. Create custom columns based on specific senders, AI labels, or topics that match exactly how you want to process your morning mail.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Group emails by project or client</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Isolate receipts for easy filing</li>
              <li className="flex items-center gap-3 text-slate-700"><IconCheck className="h-5 w-5 text-blue-600" /> Tailor the Kanban board to your brain</li>
            </ul>
          </div>
        </section>

        {/* Feature 7: Dynamic Island & Background Sync */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">The Dynamic Island.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Stay in the loop without being distracted. Our custom Dynamic Island shows real-time progress as we connect to Gmail, scan your filters, and build your personalized sorting model.
            </p>
          </div>

          <div className="flex justify-center items-center py-12 px-6 bg-slate-100 rounded-3xl border border-slate-200">
            {/* Dynamic Island Mockup */}
            <div className="flex items-center gap-3 px-4 py-2 h-10 rounded-full bg-slate-950 shadow-2xl shadow-slate-900/20">
              <svg className="h-4 w-4 text-[#f472b6] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
              </svg>
              <p className="text-xs text-white flex items-center">
                <span className="font-medium">Sorting your inbox</span>
                <span className="text-white/40 mx-1.5">·</span>
                <span className="text-white/60">42 of 108 messages</span>
              </p>
              <div className="flex items-center gap-1.5 ml-2">
                <div className="h-1 w-14 overflow-hidden rounded-full bg-white/20">
                  <div className="h-full w-[40%] bg-[#f472b6]" />
                </div>
                <span className="text-white/50 text-[10px] font-mono w-6 text-right">38%</span>
              </div>
            </div>
          </div>
        </section>

        {/* Feature 8: Dark Mode */}
        <section className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative rounded-3xl border border-slate-800 bg-slate-950 p-8 shadow-2xl">
            {/* Dark Mode Mockup */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-800">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <IconMoonStars className="h-5 w-5 text-blue-400" />
                Dark Theme Active
              </h3>
              <div className="w-12 h-6 bg-blue-600 rounded-full relative">
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <p className="text-slate-200 text-sm font-medium">Stripe</p>
                  <span className="bg-slate-800 text-slate-300 text-xs px-2 py-0.5 rounded">Receipt</span>
                </div>
                <p className="text-slate-400 text-xs mt-1">Your invoice #INV-2024 is ready...</p>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">First-class dark mode.</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Whether you're processing emails late at night or you just prefer a sleeker interface, Winbox Zero includes full support for dark mode. Colors, tags, and syntax highlighting automatically adapt to reduce eye strain.
            </p>
          </div>
        </section>

      </div>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-white px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-50 border border-slate-200 px-8 py-20 shadow-sm sm:px-16 sm:py-24 text-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-blue-100/50 blur-3xl pointer-events-none" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-bold text-slate-900 sm:text-5xl font-['Switzer',_sans-serif] tracking-[-0.01em]">
                Ready to stop dreading your inbox?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-600">
                Join the operators who let AI do the heavy lifting. Get through your morning email in 10 minutes or less.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton location="footer_cta" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  )
}

